import { useMemo } from 'react';
import { buildHeatmap, formatHour, HEATMAP_DAYS } from '../../../lib/reporting/socialContent';
import type { SocialBestTimeSlot } from '../../../types/socialReporting';

// Content tab — best time to post heatmap (day × hour, coloured by avg engagement).
// From getBestTimeToPost (CONFIRMED available, not add-on gated). recharts has no
// native heatmap, so this is a plain CSS grid. Zernio returns UTC hours; we shift
// to the viewer's local timezone (buildHeatmap, offset injected here) so the grid
// reads in the times the client actually posts.

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const KYCK_GREEN = '16, 157, 88'; // rgb — the platform GBP-green also used on charts

export function BestTimeHeatmap({ slots }: { slots: SocialBestTimeSlot[] }) {
  // getTimezoneOffset() is minutes to ADD to local to reach UTC, so the local
  // offset FROM utc is its negation (Brisbane −600 → +600).
  const tzOffsetMinutes = -new Date().getTimezoneOffset();
  const heatmap = useMemo(() => buildHeatmap(slots, tzOffsetMinutes), [slots, tzOffsetMinutes]);

  if (heatmap.isEmpty) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-slate-400">
        Not enough posting history to suggest best times yet.
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          {/* Hour axis — labelled every 3 hours. */}
          <div className="mb-1 flex pl-10 text-[10px] text-slate-400">
            {HOURS.map((h) => (
              <div key={h} className="flex-1 text-center">
                {h % 3 === 0 ? formatHour(h).replace(' ', '') : ''}
              </div>
            ))}
          </div>
          {heatmap.grid.map((row, day) => (
            <div key={day} className="mb-0.5 flex items-center">
              <div className="w-10 shrink-0 pr-2 text-right text-[11px] font-medium text-slate-500">
                {HEATMAP_DAYS[day]}
              </div>
              <div className="flex flex-1 gap-0.5">
                {row.map((cell, hour) => (
                  <div
                    key={hour}
                    className="h-5 flex-1 rounded-sm"
                    title={
                      cell
                        ? `${HEATMAP_DAYS[day]} ${formatHour(hour)} — avg ${Math.round(cell.avgEngagement).toLocaleString()} engagement (${cell.postCount} ${cell.postCount === 1 ? 'post' : 'posts'})`
                        : `${HEATMAP_DAYS[day]} ${formatHour(hour)} — no posts`
                    }
                    style={{ backgroundColor: cellColor(cell, heatmap.maxAvg) }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
        <span>Times shown in your local timezone.</span>
        <span className="flex items-center gap-1">
          Less
          <span className="h-3 w-4 rounded-sm" style={{ backgroundColor: `rgba(${KYCK_GREEN}, 0.12)` }} />
          <span className="h-3 w-4 rounded-sm" style={{ backgroundColor: `rgba(${KYCK_GREEN}, 0.5)` }} />
          <span className="h-3 w-4 rounded-sm" style={{ backgroundColor: `rgba(${KYCK_GREEN}, 1)` }} />
          More
        </span>
      </div>
    </div>
  );
}

function cellColor(cell: { avgEngagement: number } | null, maxAvg: number): string {
  if (!cell || maxAvg <= 0) return 'rgb(241, 245, 249)'; // slate-100 (no data)
  const intensity = Math.min(1, cell.avgEngagement / maxAvg);
  // Floor at 0.12 so a measured-but-low cell still reads as "had activity".
  return `rgba(${KYCK_GREEN}, ${0.12 + 0.88 * intensity})`;
}
